// PRPricing.js - Australia
import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, AlertCircle } from 'lucide-react';

const PRPricing = () => {
  const coverageItems = [
    'A review of your eligibility and a recommendation on which route to take',
    'Help with the skills assessment and the paperwork behind it',
    'Working out your points total and where it can be improved',
    'Drafting your EOI and lodging it in SkillSelect',
    'State nomination applications, where one applies to you',
    'Filling in the visa application forms and checking them over',
    'Reviewing and verifying every supporting document',
    'Following the case after lodgement and answering any requests'
  ];

  const importantNotes = [
    'Visa application charges go straight to the Department of Home Affairs',
    'Ivipass never handles government fees on your behalf',
    'Assessing authorities are paid directly for the skills assessment',
    'IELTS, PTE and TOEFL fees are settled with the test provider',
    'Every PR decision rests with the Department of Home Affairs, not with Ivipass. What we control is the quality of what gets lodged: an application built around current Departmental requirements, with the documentation complete and the claims evidenced. We do not promise an outcome, and you should be wary of anyone who does.'
  ];

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8"
          >
            <h2 className="text-3xl md:text-4xl font-semibold text-primary mb-4">
              What Our PR Support Costs
            </h2>
          </motion.div>

          {/* Pricing Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white p-8 rounded-lg shadow-sm mb-6 border border-gray-200"
          >
            <h3 className="text-xl font-semibold text-primary mb-4">
              Support for Your Australian PR Application
            </h3>
            <p className="text-gray-600 text-base font-light leading-relaxed mb-6">
              One service covers the whole Australian permanent residency application, from the first look at your eligibility through to the day everything is filed.
            </p>

            {/* Coverage List */}
            <div className="mb-6">
              <p className="text-gray-700 text-base font-normal mb-4">
                What the service covers:
              </p>
              <div className="space-y-3">
                {coverageItems.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.3 + (index * 0.1) }}
                    className="flex items-start gap-3"
                  >
                    <CheckCircle2 size={18} className="text-gray-700 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700 text-base font-light">
                      {item}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Fee Structure */}
            <div className="bg-white p-6 rounded-lg mb-4 border border-gray-200">
              <p className="text-sm font-semibold text-primary mb-4">
                Service Fee
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gradient-to-r from-primary/5 to-transparent rounded-lg">
                  <div>
                    <p className="text-lg font-semibold text-primary">PR Application Support</p>
                    <p className="text-sm text-gray-600">Full preparation and lodgement of your application</p>
                  </div>
                  <p className="text-2xl font-bold text-primary">$399</p>
                </div>
              </div>
              
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Note:</span> the fee applies to a single PR pathway. State nomination applications, and applications covering a partner or dependants, are charged separately.
                </p>
              </div>
            </div>

            <p className="text-gray-600 text-sm font-light leading-relaxed">
              We confirm the final figure with you after the consultation, once the route and the work it involves are clear.
            </p>
          </motion.div>

          {/* Important Notes */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="bg-blue-50 border border-blue-200 rounded-lg p-6"
          >
            <div className="flex items-start gap-3 mb-4">
              <AlertCircle size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-base font-semibold text-primary">
                Important notes:
              </p>
            </div>
            <div className="space-y-2 ml-8">
              {importantNotes.map((note, index) => (
                <p key={index} className="text-gray-700 text-sm font-light leading-relaxed">
                  • {note}
                </p>
              ))}
            </div>
          </motion.div>

          {/* Government Fees Reference */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.9 }}
            className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-6"
          >
            <h4 className="text-base font-semibold text-primary mb-3">
              Government Charges (Payable to the Department of Home Affairs)
            </h4>
            <div className="space-y-2 text-sm text-gray-700 font-light">
              <p>• <strong>Subclass 189 Skilled Independent:</strong> AUD $4,640 (primary applicant)</p>
              <p>• <strong>Subclass 190 Skilled Nominated:</strong> AUD $4,640 (primary applicant)</p>
              <p>• <strong>Subclass 491 Skilled Work Regional:</strong> AUD $4,640 (primary applicant)</p>
              <p>• <strong>Each further applicant aged 18 or over:</strong> AUD $2,320</p>
              <p>• <strong>Each child under 18:</strong> AUD $1,160</p>
              <p className="text-xs text-gray-600 mt-3">
                These charges change from time to time. Check the Department of Home Affairs website for the schedule in force when you apply.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default PRPricing;
